import { Button as ChakraButton, type ButtonProps as ChakraButtonProps } from '@chakra-ui/react'
import { ReactElement, forwardRef } from 'react'
import { Flex } from '.'

export interface Props extends Omit<ChakraButtonProps, 'variant' | 'justify'> {
  /** default center */
  justify?: 'center' | 'space-between'
  leftIcon?: ReactElement
  palette?: 'primary' | 'secondary' | 'danger'
  rightIcon?: ReactElement
  /** default solid */
  variant?: 'solid' | 'outline' | 'ghost' | 'link'
}

export default forwardRef<HTMLButtonElement, Props>(
  (
    {
      bg,
      background,
      backgroundColor,
      children,
      color,
      justify = 'center',
      leftIcon,
      palette,
      rightIcon,
      size = 'md',
      variant = 'solid',
      ...rest
    },
    ref
  ) => {
    const baseBorderColor = { base: 'rgba(23, 23, 23, 0.2)', _dark: 'rgba(255, 255, 255, 0.2)' }

    return (
      <ChakraButton
        ref={ref}
        bg={
          (bg || background || backgroundColor) ??
          (variant === 'solid'
            ? palette
              ? `${palette}.500`
              : { base: 'white', _dark: 'rgba(255, 255, 255, 0.14)' }
            : variant === 'outline' && !palette
            ? { base: 'white', _dark: 'rgba(255, 255, 255, 0.05)' }
            : 'transparent')
        }
        color={
          color ??
          (variant === 'solid'
            ? palette
              ? 'white'
              : undefined
            : palette
            ? `${palette}.500`
            : undefined)
        }
        borderColor={
          variant === 'outline' ? (palette ? `${palette}.500` : baseBorderColor) : undefined
        }
        borderRadius="md"
        borderWidth={variant === 'outline' ? `1px` : undefined}
        boxShadow={
          variant === 'solid' || variant === 'outline'
            ? '0px 1px 4px 0px rgba(0, 0, 0, 0.06)'
            : undefined
        }
        fontWeight="medium"
        _hover={{
          background:
            variant === 'solid'
              ? palette
                ? `${palette}.700`
                : { base: '#FFFFFF88', _dark: 'rgba(255, 255, 255, 0.24)' }
              : variant === 'link'
              ? undefined
              : palette
              ? palette === 'primary'
                ? 'rgba(60, 75, 211, 0.03)'
                : `${palette}.200`
              : baseBorderColor,
          textDecoration: variant === 'link' ? 'underline' : undefined,
        }}
        size={size}
        {...rest}
      >
        {leftIcon}
        {children && (
          <Flex overflow="hidden" fontWeight="semibold" flex={justify === 'center' ? undefined : 1}>
            {children}
          </Flex>
        )}
        {rightIcon}
      </ChakraButton>
    )
  }
)
